import React, { useState, useRef, useEffect } from 'react';
import { Row, Col, Form, InputGroup } from 'react-bootstrap';
import styled from 'styled-components';
import { BiSearch, BiChevronDown } from 'react-icons/bi';

const StyledFormGroup = styled(Form.Group)`
  margin-bottom: 1.25rem;
  .form-label {
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.5rem;
  }
  .form-control, .form-select {
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    border: 1px solid #e0e0e0;
    transition: all 0.3s ease;
    &:focus {
      border-color: #4b6cb7;
      box-shadow: 0 0 0 0.25rem rgba(75, 108, 183, 0.25);
    }
    &.is-invalid {
      border-color: #dc3545;
    }
  }
`;

const indianStates = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
];

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 0.25rem;
`;

const DropdownOption = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NoResultsMessage = styled.div`
  padding: 0.75rem 1rem;
  color: #6c757d;
  font-style: italic;
`;

const AddressInformation = ({ register, errors }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Extract the state field registration functions
  const stateField = register('address.state');

  // Filter states based on search term
  const filteredStates = indianStates.filter(state =>
    state.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle state selection
  const handleStateSelect = (state) => {
    setSearchTerm(state);
    setShowDropdown(false);
    
    // Create a synthetic event for React Hook Form
    const syntheticEvent = {
      target: {
        name: 'address.state',
        value: state
      }
    };
    
    // Update the form value
    stateField.onChange(syntheticEvent);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <h5 className="section-title">Address</h5>
      <Row>
        <StyledFormGroup as={Col} md="6" controlId="street">
          <Form.Label>Street Address <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            {...register('address.street')}
            isInvalid={!!errors.address?.street}
            placeholder="Enter street address"
          />
          <Form.Control.Feedback type="invalid" className="d-flex align-items-center">
            <i className="bi bi-exclamation-circle me-2"></i>
            {errors.address?.street?.message}
          </Form.Control.Feedback>
        </StyledFormGroup>
        <StyledFormGroup as={Col} md="3" controlId="city">
          <Form.Label>City <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            {...register('address.city')}
            isInvalid={!!errors.address?.city}
            placeholder="Enter city"
          />
          <Form.Control.Feedback type="invalid" className="d-flex align-items-center">
            <i className="bi bi-exclamation-circle me-2"></i>
            {errors.address?.city?.message}
          </Form.Control.Feedback>
        </StyledFormGroup>
        <StyledFormGroup as={Col} md="3" controlId="district">
          <Form.Label>District <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            {...register('address.district')}
            isInvalid={!!errors.address?.district}
            placeholder="Enter district"
          />
          <Form.Control.Feedback type="invalid" className="d-flex align-items-center">
            <i className="bi bi-exclamation-circle me-2"></i>
            {errors.address?.district?.message}
          </Form.Control.Feedback>
        </StyledFormGroup>
      </Row>
      <Row>
        <StyledFormGroup as={Col} md="3" controlId="state">
          <Form.Label>State <span className="text-danger">*</span></Form.Label>
          <DropdownContainer ref={dropdownRef}>
            <InputGroup>
              <Form.Control
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchTerm(value);
                  setShowDropdown(true);
                  
                  const syntheticEvent = {
                    target: {
                      name: 'address.state',
                      value: value
                    }
                  };
                  stateField.onChange(syntheticEvent);
                }}
                onFocus={() => setShowDropdown(true)}
                onClick={() => setShowDropdown(!showDropdown)}
                placeholder="Select or search state"
                isInvalid={!!errors.address?.state}
                ref={(e) => {
                  if (stateField.ref) stateField.ref(e);
                  inputRef.current = e;
                }}
                autoComplete="off"
              />
              <InputGroup.Text 
                style={{ cursor: 'pointer' }}
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <BiChevronDown />
              </InputGroup.Text>
            </InputGroup>
            
            {/* Hidden input to ensure proper form validation */}
            <Form.Control
              type="hidden"
              name="address.state"
              value={searchTerm}
              {...stateField}
            />
            
            {/* Custom dropdown for searchable states */}
            {showDropdown && (
              <DropdownMenu>
                {filteredStates.length > 0 ? (
                  filteredStates.map((state) => (
                    <DropdownOption
                      key={state}
                      onClick={() => handleStateSelect(state)}
                    >
                      {state}
                    </DropdownOption>
                  ))
                ) : (
                  <NoResultsMessage>
                    No states found matching "{searchTerm}"
                  </NoResultsMessage>
                )}
              </DropdownMenu>
            )}
            
            <Form.Control.Feedback type="invalid" className="d-flex align-items-center mt-1">
              <i className="bi bi-exclamation-circle me-2"></i>
              {errors.address?.state?.message}
            </Form.Control.Feedback>
          </DropdownContainer>
        </StyledFormGroup>
        <StyledFormGroup as={Col} md="3" controlId="postalCode">
          <Form.Label>Postal Code <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            {...register('address.postalCode')}
            isInvalid={!!errors.address?.postalCode}
            placeholder="Enter postal code"
          />
          <Form.Control.Feedback type="invalid" className="d-flex align-items-center">
            <i className="bi bi-exclamation-circle me-2"></i>
            {errors.address?.postalCode?.message}
          </Form.Control.Feedback>
        </StyledFormGroup>
        <StyledFormGroup as={Col} md="3" controlId="country">
          <Form.Label>Country</Form.Label>
          <Form.Control
            type="text"
            {...register('address.country')}
            readOnly
            defaultValue="India"
            className="bg-light"
          />
        </StyledFormGroup>
      </Row>
    </>
  );
};

export default AddressInformation;